import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdVisibility,
  MdCheck,
  MdClose,
  MdSettings,
  MdInfo,
  MdBusiness,
  MdPerson,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

const ConfirmStatusText: any = { 0: "Pending", 1: "Approved", 2: "Rejected", 3: "Expired" };
const ConfirmStatusColor: any = { 0: "#F59E0B", 1: "#16A34A", 2: "#DC2626", 3: "#6B7280" };
const ConditionText: any = { 3: "Serviceable", 4: "Unserviceable" };

const BoolBadge = ({ value }: any) => (
  <Badge
    label={value ? "Yes" : "No"}
    color={value ? "#16A34A" : "#DC2626"}
    bg={value ? "rgba(22,163,74,.15)" : "rgba(220,38,38,.15)"}
  />
);

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "28px 220px 1fr",
      gap: 10,
      padding: "6px 0",
      borderBottom: "1px solid #E5E7EB",
      fontSize: 12,
    }}
  >
    <Icon size={22} color="#25b3ebff" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value ?? "-"}</span>
  </div>
);

const Card = ({ icon: Icon, title, children }: any) => (
  <div
    style={{
      border: "1px solid #E5E7EB",
      borderRadius: 18,
      padding: 16,
      background: "#FFFFFFAA",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        background:
          "linear-gradient(135deg,#3B82F6 0%,#60A5FA 50%,#93C5FD 100%)",
        color: "#fff",
        borderRadius: 8,
        padding: 6,
      }}
    >
      <Icon size={22} />
      <strong>{title}</strong>
    </div>
    {children}
  </div>
);

const ConfirmStatusModal = ({ row, onClose, onSaved }: any) => {
  const api = useMemo(() => createApi(), []);
  const [status, setStatus] = useState(row.confirmStatus);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await api.enginesaleleasaWanted.confirmStatus(row.id, status);
    setSaving(false);
    onClose();
    onSaved();
  };

  return (
    <Modal open onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Confirm Status</div>
      <select
        value={status}
        onChange={(e) => setStatus(+e.target.value)}
        style={{ width: "100%", height: 36, marginBottom: 16 }}
      >
        <option value={0}>Pending</option>
        <option value={1}>Approved</option>
        <option value={2}>Rejected</option>
      </select>
      <div style={{ display: "flex", gap: 8 }}>
        <Button icon={MdCheck} loading={saving} onClick={save}>
          Confirm
        </Button>
        <Button icon={MdClose} variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const EngineSaleLeaseWantedDetailModal = ({ id, onClose }: any) => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    api.enginesaleleasaWanted.getById(id).then((r: any) => {
      setData(r?.data?.data ?? r?.data);
    });
  }, [id]);

  if (!data) return null;

  return (
    <Modal open onClose={onClose}>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>Engine Sale/Lease Wanted Detail</div>
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 22, marginBottom: 24 }}>
          Engine {data.engineType?.code} Wanted
        </h1>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(420px,1fr))",
            gap: 20,
          }}
        >
          <Card icon={MdInfo} title="General">
            <InfoRow
              icon={MdInfo}
              label="Description"
              value={data.description}
            />
            <InfoRow
              icon={MdInfo}
              label="Trade Type"
              value={data.tradeType === 1 ? "Sale" : "Lease"}
            />
            <InfoRow
              icon={MdInfo}
              label="Availability Date"
              value={new Date(data.availabilityDateWanted).toLocaleDateString()}
            />
            <InfoRow
              icon={MdInfo}
              label="Confirm Status"
              value={
                <Badge
                  label={ConfirmStatusText[data.confirmStatus]}
                  color={ConfirmStatusColor[data.confirmStatus]}
                />
              }
            />
          </Card>

          <Card icon={MdSettings} title="Engine Criteria">
            <InfoRow
              icon={MdSettings}
              label="Condition"
              value={
                data.conditionWanted ? ConditionText[data.conditionWanted] : "-"
              }
            />
            <InfoRow
              icon={MdSettings}
              label="Engine First Limiter Cycle"
              value={data.engineFirstLimiterCycleWanted}
            />
            <InfoRow
              icon={MdSettings}
              label="Engine Stand"
              value={<BoolBadge value={data.engineStandWanted} />}
            />
            <InfoRow
              icon={MdSettings}
              label="Lease Duration (Month)"
              value={data.leaseDurationMonthWanted}
            />
          </Card>

          <Card icon={MdBusiness} title="Buyer">
            <InfoRow
              icon={MdBusiness}
              label="Buyer Name"
              value={data.buyerName}
            />
            <InfoRow
              icon={MdInfo}
              label="Delivery Terms"
              value={data.deliveryTermsWanted}
            />
          </Card>

          <Card icon={MdPerson} title="User">
            <InfoRow
              icon={MdPerson}
              label="Username"
              value={data.user?.username}
            />
            <InfoRow icon={MdInfo} label="Email" value={data.user?.email} />
          </Card>

          <Card icon={MdBusiness} title="Company">
            <InfoRow
              icon={MdBusiness}
              label="Company"
              value={data.company?.companyName}
            />
          </Card>
        </div>
      </div>
    </Modal>
  );
};

export const EngineSaleLeaseWantedForms = () => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [statusRow, setStatusRow] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r: any = await api.enginesaleleasaWanted.getAll();
    setData(r?.data?.data ?? r?.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: "engine",
        title: "Engine",
        accessor: (r: any) => r.engineType?.code,
      },
      {
        key: "description",
        title: "Description",
        accessor: (r: any) => r.description,
      },
      {
        key: "tradeType",
        title: "Trade Type",
        accessor: (r: any) => (r.tradeType === 1 ? "Sale" : "Lease"),
      },
      {
        key: "buyer",
        title: "Buyer",
        accessor: (r: any) => r.buyerName,
      },
      {
        key: "company",
        title: "Company",
        accessor: (r: any) => r.company?.companyName,
      },
      {
        key: "status",
        title: "Status",
        accessor: (r: any) => r.confirmStatus,
        render: (_: any, v: any) => (
          <Badge
            label={ConfirmStatusText[v]}
            color={ConfirmStatusColor[v]}
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        render: (r: any) => (
          <GroupButton
            items={[
              {
                key: "detail",
                icon: MdVisibility,
                color: "#2563EB",
                onClick: () => setDetailId(r.id),
              },
              {
                key: "confirm",
                icon: MdCheck,
                color: "#F59E0B",
                onClick: () => setStatusRow(r),
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          Engine Sale/Lease Wanted
        </div>
        <div style={{ fontSize: 13, color: "#64748B" }}>
          Manage wanted engines
        </div>
      </div>

      <Table loading={loading} columns={columns} data={data} />

      {detailId && (
        <EngineSaleLeaseWantedDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      {statusRow && (
        <ConfirmStatusModal
          row={statusRow}
          onClose={() => setStatusRow(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};
